import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "../css/Consent.css";

const Consent = () => {
  const navigate = useNavigate();
  const { createLearnerWithConsent, linkLearner, fetchParentDashboard } = useAuth();

  const [mode, setMode] = useState("link"); // "link" | "create"
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ---------- Link by code (institution-issued) ---------- */
  const [learnerCode, setLearnerCode] = useState("");

  /* ---------- Create new standalone child ---------- */
  const [form, setForm] = useState({
    child_name: "",
    grade: "",
    section: "",
    school: "",
    dob: "",
    accepted_policy: false,
  });

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /* ---------- Validate create form ---------- */
  const validateCreate = () => {
    if (!form.child_name.trim()) return "Child name is required";
    if (!form.grade.trim()) return "Grade is required";
    if (!form.school.trim()) return "School is required";
    if (!form.accepted_policy) return "You must accept the Privacy Policy";
    return null;
  };

  /* ---------- Submit: link ---------- */
  const handleLink = async () => {
    if (!learnerCode.trim()) return alert("Please enter the learner code");
    setLoading(true);
    try {
      const result = await linkLearner(learnerCode.trim());
      if (result?.message?.toLowerCase().includes("success") || result?.learner_id) {
        await fetchParentDashboard();
        setApproved(true);
      } else {
        alert(result?.message || "Invalid learner code. Ask the school for the correct code.");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to link learner. Check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Submit: create ---------- */
  const handleCreate = async () => {
    const error = validateCreate();
    if (error) return alert(error);
    setLoading(true);
    try {
      await createLearnerWithConsent(form);
      await fetchParentDashboard();
      setApproved(true);
    } catch (err) {
      console.error(err);
      alert("Failed to create child account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ===================== SUCCESS STATE ===================== */
  if (approved) {
    return (
      <div className="consent-container">
        <div className="consent-card" style={{ textAlign: "center" }}>
          <h1>Child Added Successfully</h1>
          <p>
            {mode === "link"
              ? "Your child's school account has been linked to your dashboard."
              : "Your child's account has been created and linked to your dashboard."}
          </p>
          <button className="btn" style={{ marginTop: 20 }} onClick={() => navigate("/parent")}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  /* ===================== TABS ===================== */
  return (
    <div className="consent-container">
      <div className="consent-card">
        <h1>Add Child</h1>
        <p>Choose how to add your child — link an existing school account or create a new one.</p>

        {/* ---- Tab switcher ---- */}
        <div style={{ display: "flex", gap: 8, margin: "20px 0 24px" }}>
          <button
            className={`btn${mode === "link" ? "" : " secondary"}`}
            style={{ flex: 1 }}
            onClick={() => setMode("link")}
          >
            Link School Child
          </button>
          <button
            className={`btn${mode === "create" ? "" : " secondary"}`}
            style={{ flex: 1 }}
            onClick={() => setMode("create")}
          >
            Add New Child
          </button>
        </div>

        {/* ================== LINK FLOW ================== */}
        {mode === "link" && (
          <>
            <p style={{ fontSize: 14, color: "#555", marginBottom: 8 }}>
              Your child's school (institution) created an account for them and gave you a <strong>learner code</strong>. Enter it below to link them to your dashboard.
            </p>
            <div className="consent-form">
              <input
                placeholder="Learner Code (e.g. ABC-1234)"
                value={learnerCode}
                onChange={(e) => setLearnerCode(e.target.value)}
              />
            </div>
            <div className="consent-buttons">
              <button className="btn" onClick={handleLink} disabled={loading}>
                {loading ? "Linking..." : "Link Child"}
              </button>
              <button className="btn secondary" onClick={() => navigate("/parent")}>
                Cancel
              </button>
            </div>
          </>
        )}

        {/* ================== CREATE FLOW ================== */}
        {mode === "create" && (
          <>
            <p style={{ fontSize: 14, color: "#555", marginBottom: 8 }}>
              Your child is not registered at a school on BeIntelli. Fill in the details below to create their account.
            </p>
            <div className="consent-form">
              <input
                placeholder="Child Name *"
                value={form.child_name}
                onChange={(e) => handleChange("child_name", e.target.value)}
              />
              <input
                placeholder="Grade / Class *"
                value={form.grade}
                onChange={(e) => handleChange("grade", e.target.value)}
              />
              <input
                placeholder="Section"
                value={form.section}
                onChange={(e) => handleChange("section", e.target.value)}
              />
              <input
                placeholder="School Name *"
                value={form.school}
                onChange={(e) => handleChange("school", e.target.value)}
              />
              <label>Date of Birth</label>
              <input
                type="date"
                value={form.dob}
                onChange={(e) => handleChange("dob", e.target.value)}
              />

              <div className="checkbox" style={{ marginTop: 12 }}>
                <input
                  type="checkbox"
                  checked={form.accepted_policy}
                  onChange={(e) => handleChange("accepted_policy", e.target.checked)}
                />
                <label>
                  I confirm I have read the{" "}
                  <a href="/PRIVACY-POLICY.pdf" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>{" "}
                  and consent to my child's participation in assessments on BeIntelli.
                </label>
              </div>
            </div>

            <div className="consent-buttons">
              <button className="btn" onClick={handleCreate} disabled={loading}>
                {loading ? "Creating..." : "Create Child Account"}
              </button>
              <button className="btn secondary" onClick={() => navigate("/parent")}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Consent;
